import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import type { GamificationEventsQuery, UpdateGamificationConfigRequest } from '@troia-v3/types';

import { gamificationApi } from '../services/gamificationApi';

export const GAMIFICATION_KEYS = {
  config: ['gamification', 'config'] as const,
  events: (query: GamificationEventsQuery) => ['gamification', 'events', query] as const,
  summary: (periodKey?: string) => ['gamification', 'events-summary', periodKey ?? 'current'] as const,
};

/** Config de gamificação (`GET /api/gamification/config`) — `gamification:read`. */
export const useGamificationConfig = (enabled = true) =>
  useQuery({ queryKey: GAMIFICATION_KEYS.config, queryFn: () => gamificationApi.getConfig(), staleTime: 60_000, enabled });

/** Salva a config (`PUT /api/gamification/config`) — invalida ranking/me junto (mudam com visibilidade/recortes/período). */
export const useUpdateGamificationConfig = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (patch: UpdateGamificationConfigRequest) => gamificationApi.updateConfig(patch),
    onSuccess: (config) => {
      queryClient.setQueryData(GAMIFICATION_KEYS.config, config);
      queryClient.invalidateQueries({ queryKey: ['gamification'] });
      toast.success('Configurações de gamificação salvas');
    },
    onError: (error: unknown) => {
      const message = (error as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Erro ao salvar a configuração';
      toast.error(message);
    },
  });
};

/** Auditoria paginada (`GET /api/gamification/events`) — `gamification:read`. */
export const useGamificationEvents = (query: GamificationEventsQuery, enabled = true) =>
  useQuery({
    queryKey: GAMIFICATION_KEYS.events(query),
    queryFn: () => gamificationApi.listEvents(query),
    staleTime: 15_000,
    enabled,
    placeholderData: (prev) => prev,
  });

/** KPIs da auditoria (`GET /api/gamification/events/summary`) — `gamification:read`. */
export const useGamificationEventsSummary = (periodKey?: string, enabled = true) =>
  useQuery({ queryKey: GAMIFICATION_KEYS.summary(periodKey), queryFn: () => gamificationApi.getEventsSummary(periodKey), staleTime: 30_000, enabled });
